import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { VoltApiError } from "../voltApi";
import { DraftView } from "./DraftView";
import { WorkshopError } from "./WorkshopError";
import { WorkshopFrame, type WorkshopMeta } from "./WorkshopFrame";
import { errorText, workshopApi } from "./workshopApi";
import type { Draft } from "./types";

export const WorkshopDraftPage = () => (
  <WorkshopFrame>{(meta) => <DraftLoader meta={meta} />}</WorkshopFrame>
);

const DraftLoader = ({ meta }: { meta: WorkshopMeta }) => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // A dead link (deleted or no access) goes back to the overview; other errors show.
  useEffect(() => {
    let live = true;
    setDraft(null);
    setError(null);
    workshopApi
      .draft(id)
      .then((d) => live && setDraft(d))
      .catch((e) => {
        if (!live) return;
        if (e instanceof VoltApiError && [403, 404].includes(e.status))
          navigate("/workshop", { replace: true });
        else setError(errorText(e));
      });
    return () => {
      live = false;
    };
  }, [id, navigate, attempt]);

  const onUpdated = useCallback((d: Draft) => setDraft(d), []);

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <WorkshopError>{error}</WorkshopError>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            Neu laden
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/workshop">Alle Entwürfe</Link>
          </Button>
        </div>
      </div>
    );
  }
  if (!draft) {
    return (
      <div className="flex flex-col gap-3" aria-busy>
        <Skeleton className="h-8 w-96 max-w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  return (
    <DraftView
      draft={draft}
      meta={meta}
      onUpdated={onUpdated}
      onDeleted={() => navigate("/workshop")}
    />
  );
};

// Old Werkstatt hash path #/entwurf/<id> keeps working inside the CRM.
export const LegacyDraftRedirect = () => {
  const { id = "" } = useParams();
  return <Navigate to={`/workshop/draft/${id}`} replace />;
};
